import { useEffect, useState, type ReactElement } from 'react'
import { ConfirmationAudit } from './ConfirmationAudit'

/**
 * Spec 160 (FR-023) — une confirmation d'audit à placer devant n'importe quel bouton : `confirmer(nomClient, action)`
 * ouvre la boîte ; l'action ne part qu'au clic sur « Confirmer ». `element` se rend une fois dans le composant.
 *
 * `contexte` — ce que la page montre (le client affiché, l'identifiant de la fiche) : la confirmation ne vaut que pour
 * le contexte où elle a été ouverte. Il change, elle est abandonnée, et ne revient pas avec lui (revue à froid de
 * l'incrément 4 : ouverte sur A, un retour arrière vers B la rouvrait seule une fois B chargé, et « Confirmer » lançait
 * l'audit de A). Une liste où chaque ligne nomme son client n'en a pas besoin.
 */
export function useConfirmationAudit(nature: 'demande' | 'lancement', contexte: string | null = null): {
  confirmer: (nomClient: string, action: () => void) => void
  element: ReactElement
} {
  const [cible, setCible] = useState<{ contexte: string | null; nomClient: string; action: () => void } | null>(null)
  useEffect(() => { setCible(null) }, [contexte])
  // Dès le rendu où le contexte change, sans attendre l'effet : la boîte ne s'affiche pas une image de trop.
  const active = cible !== null && cible.contexte === contexte ? cible : null
  const element = (
    <ConfirmationAudit
      ouverte={active !== null}
      nature={nature}
      nomClient={active?.nomClient ?? ''}
      onConfirmer={() => {
        const action = active?.action
        setCible(null)
        action?.()
      }}
      onAnnuler={() => setCible(null)}
    />
  )
  return { confirmer: (nomClient, action) => setCible({ contexte, nomClient, action }), element }
}
